import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { getWsjCalDemoBookingUrl } from '@/lib/wsjCalUrl';
import { getWsjCommercialWhatsappUrl } from '@/lib/wsjCommercialWhatsapp';

const Hero = () => {
  const { t, language } = useLanguage();
  const currentLang = (language || 'pt-BR').split('-')[0];
  const whatsappMessages = {
    pt: 'Olá! Gostaria de saber mais sobre os serviços da WSJ Soluções Digitais.',
    en: 'Hello! I would like to know more about WSJ Digital Solutions services.',
    es: '¡Hola! Me gustaría saber más sobre los servicios de WSJ Soluciones Digitales.',
    fr: 'Bonjour ! Je voudrais en savoir plus sur les services de WSJ Solutions Digitales.',
    it: 'Ciao! Vorrei saperne di più sui servizi di WSJ Soluzioni Digitali.',
    hi: 'नमस्ते! मैं WSJ Digital Solutions की सेवाओं के बारे में और जानना चाहता/चाहती हूं।'
  };

  const handleWhatsApp = () => {
    const message = whatsappMessages[currentLang] || whatsappMessages.pt;
    window.open(getWsjCommercialWhatsappUrl(message), '_blank', 'noopener,noreferrer');
  };

  const scheduleDemoUrl = getWsjCalDemoBookingUrl();

  return (
    <section className="relative min-h-screen w-full min-w-0 flex flex-col justify-center overflow-hidden pt-6 sm:pt-0">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 tech-grid opacity-30"></div>
      
      {/* Blue Glow Effects - More Subtle */}
      <div className="absolute top-1/3 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/10 rounded-full blur-3xl animate-glow-pulse"></div>
      <div className="absolute bottom-1/3 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-400/10 rounded-full blur-3xl animate-glow-pulse" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 sm:w-96 sm:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-glow-pulse" style={{ animationDelay: '4s' }}></div>
      
      {/* Glowing Lines */}
      <div className="glow-line pointer-events-none w-64 top-1/4 left-1/4 animate-wave"></div>
      <div className="glow-line pointer-events-none w-96 bottom-1/3 right-1/4 animate-wave" style={{ animationDelay: '5s' }}></div>

      {/* Content — full-width wrapper + centered column (avoids horizontal flex offset on some viewports) */}
      <div className="relative z-10 w-full max-w-full box-border px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="w-full max-w-5xl mx-auto text-center">
          {/* Logo/Symbol - Minimal */}
          <div className="flex justify-center mb-12 animate-float">
            <div className="w-20 h-20 border-2 border-yellow-500/50 rounded-lg flex items-center justify-center backdrop-blur-sm bg-white/5">
              <div className="text-4xl font-black">
                <span className="text-white">W</span>
                <span className="text-red-600">.</span>
              </div>
            </div>
          </div>

          {/* Main Heading - Cleaner, Less Text */}
          <h1 className="text-center mb-8 animate-fade-in">
            <div className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-white mb-4 sm:mb-6 leading-[1.1] tracking-tight break-words">
              {t.hero.title}
            </div>
            <div className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[1.1] tracking-tight break-words">
              <span className="gradient-text-gold">
                {t.hero.titleHighlight}
              </span>
            </div>
          </h1>

          {/* Subtitle - Much Shorter */}
          <p className="text-center text-base sm:text-2xl text-white/50 mb-10 sm:mb-12 max-w-2xl mx-auto leading-relaxed font-light px-1 sm:px-2 break-words">
            {t.hero.subtitle}
          </p>

          {/* CTA Buttons - Gold Primary */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mb-16 sm:mb-24 w-full max-w-xl mx-auto">
            <Button
              size="lg"
              asChild
              className="group w-full sm:w-auto bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-slate-900 font-bold px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg rounded-xl shadow-2xl hover:shadow-yellow-500/50 transition-all duration-300 transform hover:scale-105 glow-gold-hover"
            >
              <a
                href={scheduleDemoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center"
              >
                {t.hero.cta}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden />
              </a>
            </Button>

            <Button
              size="lg"
              onClick={handleWhatsApp}
              className="group w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-semibold px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg rounded-xl transition-all duration-300"
            >
              <MessageCircle className="mr-2 w-5 h-5" />
              {t.hero.ctaSecondary}
            </Button>
          </div>

          {/* Stats - Minimal and Elegant */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-8 max-w-3xl mx-auto w-full">
            <div className="text-center group cursor-default">
              <div className="text-4xl sm:text-6xl font-black text-white mb-2">
                10<span className="text-yellow-500">+</span>
              </div>
              <div className="text-xs sm:text-sm text-white/40 uppercase tracking-wider font-medium">
                {t.about.yearsTitle}
              </div>
            </div>
            <div className="text-center group cursor-default">
              <div className="text-4xl sm:text-6xl font-black text-white mb-2">
                100<span className="text-yellow-500">+</span>
              </div>
              <div className="text-xs sm:text-sm text-white/40 uppercase tracking-wider font-medium">
                {t.about.clientsTitle}
              </div>
            </div>
            <div className="text-center group cursor-default">
              <div className="text-4xl sm:text-6xl font-black text-white mb-2">
                200<span className="text-yellow-500">+</span>
              </div>
              <div className="text-xs sm:text-sm text-white/40 uppercase tracking-wider font-medium">
                {t.about.projectsTitle}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a1628] to-transparent"></div>
    </section>
  );
};

export default Hero;
